import React, { Fragment, type ReactNode } from 'react';

import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { usePanelStateInternal } from '../panel-manager/use-panel-state-internal';

import { PanelContext } from './panel-context';

/**
 * Props for the PanelsRenderer component
 */
export type PanelsRendererProps = {
	/**
	 * Component to render for each active panel.
	 * Each panel instance is automatically wrapped in PanelContext.Provider,
	 * so you can use useCurrentPanel() within your component to access panel data.
	 *
	 * @example
	 * ```tsx
	 * <PanelsRenderer>
	 *   <CustomPanel />
	 * </PanelsRenderer>
	 *
	 * function CustomPanel() {
	 *   const panel = useCurrentPanel();
	 *
	 *   return (
	 *     <PanelContainer>
	 *       <PanelHeader>
	 *         <PanelTitle>{panel.params?.title}</PanelTitle>
	 *         <PanelActionClose />
	 *       </PanelHeader>
	 *       <PanelBody>{panel.params?.content}</PanelBody>
	 *     </PanelContainer>
	 *   );
	 * }
	 * ```
	 */
	children?: ReactNode;
};

/**
 * PanelsRenderer component that renders all active panels.
 *
 * This is the companion to the Trigger component. While Trigger opens panels,
 * PanelsRenderer displays all currently open panels.
 *
 * Each panel instance is automatically wrapped in PanelContext.Provider, enabling
 * useCurrentPanel() for clean access to panel data without prop drilling.
 *
 * Simply provide a component as children. PanelsRenderer will automatically render
 * it for each active panel, with each instance wrapped in its own context.
 *
 * Use panel-system UI components (PanelContainer, PanelHeader, PanelBody, etc.)
 * to compose your panel UI within your component.
 *
 * Must be used within a PanelProvider.
 *
 * Performance note: When activePanels changes, all panels re-render because
 * the panels array in the map is new. This is negligible because:
 * - The entire map must re-run anyway (unavoidable)
 * - Most apps have <5 panels open simultaneously
 * - Panel re-renders are cheap (just UI components)
 * - The cleaner API (context) justifies the minimal perf cost
 *
 * @example
 * ```tsx
 * <PanelsRenderer>
 *   <CustomPanel />
 * </PanelsRenderer>
 *
 * function CustomPanel() {
 *   const panel = useCurrentPanel();
 *
 *   return (
 *     <PanelContainer>
 *       <PanelHeader>
 *         <PanelTitle>{panel.params?.title}</PanelTitle>
 *         <PanelActionClose />
 *       </PanelHeader>
 *       <PanelBody>{panel.params?.content}</PanelBody>
 *     </PanelContainer>
 *   );
 * }
 * ```
 */
export function PanelsRenderer({ children }: PanelsRendererProps): React.JSX.Element | null {
	const state = usePanelStateInternal();

	const isMotionUpliftEnabled = fg('platform-dst-motion-uplift-panel');

	const renderedPanels = state.activePanels.map((panel) => (
		<PanelContext.Provider key={panel.instanceId} value={panel}>
			{children}
		</PanelContext.Provider>
	));

	if (isMotionUpliftEnabled) {
		/**
		 * `ExitingPersistence` keeps a closing panel mounted until its exit motion
		 * (driven by `useMotion` inside the panel) finishes, then removes it. Each panel
		 * is keyed by `instanceId`, so `ExitingPersistence` can diff removals correctly.
		 */
		return <ExitingPersistence>{renderedPanels}</ExitingPersistence>;
	}

	// If there are no panels, render nothing
	if (state.activePanels.length === 0) {
		return null;
	}

	return <Fragment>{renderedPanels}</Fragment>;
}
